import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AdminSubpage from "../components/AdminSubpage";
import { auctionsAPI } from "../services/api";
import "../components/Auctions.css";

const AuctionLivePage = () => {
  const { auctionId } = useParams();
  const [auction, setAuction] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);

  const refresh = () => {
    auctionsAPI.getById(auctionId).then((res) => {
      const d = res.data.data;
      setAuction(d);
      setCurrentItem((d.items || []).find((i) => i.id === d.current_item_id) || null);
    });
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [auctionId]);

  const goLive = async () => {
    try {
      await auctionsAPI.start(auctionId);
      toast.success("Auction is live!");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not start auction");
    }
  };

  const nextItem = async () => {
    try {
      await auctionsAPI.nextItem(auctionId);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed");
    }
  };

  const extend = async () => {
    try {
      await auctionsAPI.extendItem(auctionId);
      toast.success("Extended +2 min");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed");
    }
  };

  const end = async () => {
    if (!window.confirm("Force end this auction?")) return;
    try {
      await auctionsAPI.end(auctionId);
      toast.success("Auction ended");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed");
    }
  };

  if (!auction) {
    return (
      <AdminSubpage title="Live control" backTo="/auctions">
        <div className="loading">Loading…</div>
      </AdminSubpage>
    );
  }

  const itemCount = auction.items?.length ?? auction.item_count ?? 0;
  const pendingCount = (auction.items || []).filter((i) => i.status === "pending").length;

  return (
    <AdminSubpage title={`Live control — ${auction.title}`} backTo="/auctions">
      <div className="admin-page-panel modal-content">
        {itemCount === 0 ? (
          <div className="auction-alert auction-alert-warning">
            No items saved for this auction.{" "}
            <Link to={`/auctions/${auctionId}/edit`}>Add products and save</Link> before going live.
          </div>
        ) : pendingCount === 0 && auction.status !== "live" ? (
          <div className="auction-alert auction-alert-info">
            {itemCount} item(s) saved. Press <strong>GO LIVE</strong> to reset and start the auction
            {auction.status === "ended" ? " again" : ""}.
          </div>
        ) : null}

        <div className="auction-live-stats">
          <div className="auction-live-stat">
            <label>Status</label>
            <strong>{auction.status}</strong>
          </div>
          <div className="auction-live-stat">
            <label>Total items</label>
            <strong>{itemCount}</strong>
          </div>
          <div className="auction-live-stat">
            <label>Pending items</label>
            <strong>{pendingCount}</strong>
          </div>
        </div>

        {auction.items?.length > 0 ? (
          <ul className="auction-items-preview">
            {auction.items.map((item) => (
              <li key={item.id}>
                {item.title || item.product_id} — AED {item.starting_price} ({item.status})
              </li>
            ))}
          </ul>
        ) : null}

        <div style={{ marginBottom: 20, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn btn-primary btn-lg"
            onClick={goLive}
            disabled={itemCount === 0}
          >
            GO LIVE
          </button>
          <Link
            to={`/auctions/${auctionId}/bids`}
            className="btn btn-secondary btn-lg"
          >
            View all bids
          </Link>
        </div>

        {currentItem ? (
          <div className="auction-live-panel">
            <h3>{currentItem.title || "Current item"}</h3>
            <div className="auction-live-stats">
              <div className="auction-live-stat">
                <label>Current price</label>
                <strong>AED {currentItem.current_price}</strong>
              </div>
              <div className="auction-live-stat">
                <label>Bids</label>
                <strong>{currentItem.bid_count ?? 0}</strong>
              </div>
              <div className="auction-live-stat">
                <label>Ends</label>
                <strong>
                  {currentItem.ends_at
                    ? new Date(currentItem.ends_at).toLocaleTimeString()
                    : "—"}
                </strong>
              </div>
            </div>
            <div className="actions">
              <button type="button" className="btn btn-secondary" onClick={nextItem}>
                Next Item →
              </button>
              <button type="button" className="btn btn-secondary" onClick={extend}>
                Extend +2 min
              </button>
              <button
                type="button"
                className="btn btn-warning"
                onClick={() => auctionsAPI.markUnsold(auctionId).then(refresh)}
              >
                Mark Unsold
              </button>
            </div>
          </div>
        ) : (
          <p style={{ color: "var(--text-muted)" }}>No active item — press GO LIVE to start the first item.</p>
        )}

        <button type="button" className="btn btn-danger" onClick={end} style={{ marginTop: 16 }}>
          FORCE END
        </button>
      </div>
    </AdminSubpage>
  );
};

export default AuctionLivePage;
